import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { mapToHttpParams } from '@bcl/design-system';
import { environment } from 'src/environments';
import { Action, ActionsList, TimeOnly } from './action.model';
import { DateRange } from 'src/app/shared-components/components/notes-list/notes.model';
import { DictionaryItem } from 'src/app/services/dictionary.service';

export type GetActionsParametersDto = Readonly<{
  parentObject?: string | null;
  parentID?: number | null;

  typeID?: string | null;
  statusID?: string | null;
  assigneeID?: string | null;
  pageNumber: number;
  pageSize: number;
  dateFilter: DateRange;
}>;

export type CreateActionDto = Readonly<
  Omit<UpdateActionDto, 'id'> & { parentObject: string; parentID: number }
>;

export type UpdateActionDto = Readonly<{
  id: number;
  number: string;
  actionTypeID: string;
  statusID: string;
  responsibilityID: string | undefined;
  deadline: string | undefined;
  deadlineTime: TimeOnly | undefined;
  duration: TimeOnly | undefined;
  title: string;
  description: string | undefined;
  assigneeID: string | undefined;
  extended?: boolean;
  customerID: number;
  locationID: number;
  areaID: number;
  contactId: string | undefined;
  initialApprovalBy: string | undefined;
  initialApprovalDate: string | undefined;
  finalApprovalBy: string | undefined;
  finalApprovalDate: string | undefined;
  reason: string | undefined;
  priorityId: string | undefined;
  mapX?: number | undefined;
  mapY?: number | undefined;
}>;

@Injectable({ providedIn: 'root' })
export class ActionApiService {
  constructor(private readonly http: HttpClient) {}

  public getActions(filter: GetActionsParametersDto): Observable<ActionsList> {
    const params: GetActionsParametersDto = {
      ...filter,
      dateFilter: filter.dateFilter ?? '4',
      statusID: filter.statusID ?? '0',
      parentObject: filter.parentObject ?? 'CRM.Leads',
      pageNumber: filter.pageNumber ?? 1,
      pageSize: filter.pageSize ?? 10,
    };

    return this.http
      .get<ActionsList>(`${environment.apiHost}/actionPoints/list`, {
        params: mapToHttpParams(params),
      })
      .pipe(
        map((x) => ({
          ...x,
          actions: x.actions ?? [],
        }))
      );
  }

  public getAction(id: number): Observable<Action> {
    return this.http.get<Action>(`${environment.apiHost}/actionPoints/${id}`);
  }

  public getContacts(id: number): Observable<DictionaryItem[]> {
    return this.http.get<DictionaryItem[]>(
      `${environment.apiHost}/dictionary/contacts`,
      { params: { customerId: id } }
    );
  }

  public createAction(action: CreateActionDto): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(
      `${environment.apiHost}/actionPoints`,
      action
    );
  }

  public updateAction(action: UpdateActionDto): Observable<void> {
    return this.http
      .patch(`${environment.apiHost}/actionPoints`, action)
      .pipe(map(() => void 0));
  }

  public deleteAction(actionId: number): Observable<void> {
    return this.http
      .delete(`${environment.apiHost}/actionPoints/${actionId}`)
      .pipe(map(() => void 0));
  }

  public patchActionPointEventId(
    actionId: number,
    eventId: string
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiHost}/actionPoints/${actionId}/eventId`,
      {
        eventId,
      }
    );
  }

  public getUserEmail(
    userId: string | undefined
  ): Observable<string | undefined> {
    if (userId === undefined) {
      return of(undefined);
    }

    return this.http
      .get<{ email: string | undefined | null }>(
        `${environment.apiHost}/users/email/${userId}`
      )
      .pipe(map((x) => x.email ?? undefined));
  }
}
